import React, { useState, useEffect } from 'react';
import { List, Card, Button, Typography, Space, Descriptions, Modal, Input, message } from 'antd';
import { ArrowLeftOutlined, ScanOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const { Title, Text } = Typography;

const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [productInfo, setProductInfo] = useState(null);
  const [scanModalVisible, setScanModalVisible] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [scanList, setScanList] = useState([]);
  const [scannedItems, setScannedItems] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    // 监听全局回车事件（模拟扫码枪）
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && scanModalVisible && scanInput.trim()) {
        handleScanAdd();
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [scanModalVisible, scanInput]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 从路由状态获取商品信息，或模拟API调用
      const productData = location.state?.productInfo || {
        id: id,
        productName: '示例商品',
        productCode: 'SKU12345678',
        specification: '500ml',
        quantity: 100,
        scannedQuantity: 0,
        unit: '件',
        price: '25.80'
      };
      
      setProductInfo(productData);
      
      // 模拟已扫描的商品列表
      const mockScannedItems = Array.from({ length: 3 }, (_, index) => ({
        id: `SCAN${String(index + 1).padStart(6, '0')}`,
        barcode: `${productData.productCode}${String(index + 1).padStart(3, '0')}`,
        scanTime: new Date(Date.now() - Math.random() * 60 * 60 * 1000).toLocaleTimeString(),
        quantity: 1
      }));
      
      setScannedItems(mockScannedItems);
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    const outboundId = location.state?.outboundId;
    if (outboundId) {
      navigate(`/outbound/${outboundId}`);
    } else {
      navigate('/outbound');
    }
  };

  const handleScanClick = () => {
    setScanModalVisible(true);
    setScanList([]);
    setScanInput('');
    // 延迟聚焦，确保模态框完全打开
    setTimeout(() => {
      const input = document.querySelector('.scan-input input');
      if (input) {
        input.focus();
      }
    }, 100);
  };

  const handleScanAdd = () => {
    if (!scanInput.trim()) return;
    
    const newItem = {
      id: `TEMP${Date.now()}`,
      barcode: scanInput.trim(),
      scanTime: new Date().toLocaleTimeString(),
      quantity: 1
    };
    
    // 添加到扫描列表顶部
    setScanList(prev => [newItem, ...prev]);
    setScanInput('');
    
    // 重新聚焦输入框
    setTimeout(() => {
      const input = document.querySelector('.scan-input input');
      if (input) {
        input.focus();
      }
    }, 50);
  };

  const handleScanRemove = (itemId) => {
    setScanList(prev => prev.filter(item => item.id !== itemId));
  };

  const handleScanConfirm = () => {
    if (scanList.length === 0) {
      message.warning('请先扫描商品');
      return;
    }
    
    // 将扫描列表添加到已扫描商品中
    setScannedItems(prev => [...scanList, ...prev]);
    
    // 更新商品扫描数量
    setProductInfo(prev => ({
      ...prev,
      scannedQuantity: prev.scannedQuantity + scanList.length
    }));
    
    setScanModalVisible(false);
    setScanList([]);
    message.success(`成功扫描 ${scanList.length} 件商品`);
  };

  const handleScanCancel = () => {
    setScanModalVisible(false);
    setScanList([]);
    setScanInput('');
  };

  const renderScannedItem = (item) => (
    <List.Item
      style={{ 
        borderRadius: 8,
        marginBottom: 8,
        background: '#f9f9f9',
        border: '1px solid #e8e8e8',
        padding: '12px 16px'
      }}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong>{item.barcode}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 14 }}>
              扫描时间: {item.scanTime}
            </Text>
          </div>
          <Text style={{ fontSize: 16, color: '#52c41a' }}>
            {item.quantity} 件
          </Text>
        </div>
      </div>
    </List.Item>
  );

  const renderScanListItem = (item) => (
    <List.Item
      style={{ 
        borderRadius: 8,
        marginBottom: 8,
        background: '#e6f7ff',
        border: '1px solid #91d5ff',
        padding: '12px 16px'
      }}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text strong style={{ color: '#1890ff' }}>{item.barcode}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 14 }}>
            {item.scanTime}
          </Text>
        </div>
        <Space>
          <Text style={{ fontSize: 16, color: '#1890ff' }}>
            {item.quantity} 件
          </Text>
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleScanRemove(item.id)}
            size="small"
          />
        </Space>
      </div>
    </List.Item>
  );

  return (
    <div>
      {/* 页面头部 */}
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
            size="large"
          />
          <Title level={3} style={{ margin: 0 }}>
            货品明细
          </Title>
        </Space>
      </div>

      {/* 页面内容 */}
      <div className="page-content">
        {/* 商品信息卡片 */}
        {productInfo && (
          <Card 
            style={{ marginBottom: 24, borderRadius: 12 }}
            bodyStyle={{ padding: '20px 24px' }}
          >
            <Descriptions column={1} size="middle">
              <Descriptions.Item label="商品名称">
                <Text strong style={{ fontSize: 16 }}>
                  {productInfo.productName}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="商品编码">
                {productInfo.productCode}
              </Descriptions.Item>
              <Descriptions.Item label="规格">
                {productInfo.specification}
              </Descriptions.Item>
              <Descriptions.Item label="应出数量">
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {productInfo.quantity} {productInfo.unit}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="已扫数量">
                <Text strong style={{ 
                  fontSize: 16, 
                  color: productInfo.scannedQuantity >= productInfo.quantity ? '#52c41a' : '#faad14' 
                }}>
                  {productInfo.scannedQuantity} {productInfo.unit}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Button 
                type="primary" 
                size="large"
                icon={<ScanOutlined />}
                onClick={handleScanClick}
                style={{ 
                  width: 200, 
                  height: 48, 
                  fontSize: 16,
                  borderRadius: 8
                }}
              >
                扫码
              </Button>
            </div>
          </Card>
        )}

        {/* 已扫描商品列表 */}
        <Card 
          title={`已扫描商品 (${scannedItems.length})`}
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          {scannedItems.length > 0 ? (
            <List
              dataSource={scannedItems}
              renderItem={renderScannedItem}
              style={{ background: 'transparent' }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              暂无扫描记录
            </div>
          )}
        </Card>
      </div>

      {/* 扫码弹窗 */}
      <Modal
        title="扫码录入"
        open={scanModalVisible}
        onCancel={handleScanCancel}
        footer={null}
        width={600}
        className="scan-modal"
        destroyOnClose
      >
        <div>
          <Input
            className="scan-input"
            placeholder="请扫描商品条码或手动输入"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            onPressEnter={handleScanAdd}
            size="large"
            autoFocus
            style={{ marginBottom: 16 }}
          />
          
          <div className="scan-list" style={{ minHeight: 200 }}>
            {scanList.length > 0 ? (
              <List
                dataSource={scanList}
                renderItem={renderScanListItem}
                style={{ background: 'transparent' }}
              />
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 0', 
                color: '#999',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px dashed #d9d9d9'
              }}>
                <ScanOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>请扫描商品条码</div>
              </div>
            )}
          </div>
          
          <div className="scan-actions">
            <Button onClick={handleScanCancel} size="large">
              取消
            </Button>
            <Button 
              type="primary" 
              onClick={handleScanConfirm}
              size="large"
              disabled={scanList.length === 0}
            >
              确定 ({scanList.length})
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProductDetail;